import React from 'react';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-blue-600" />
              <h1 className="text-2xl font-semibold text-gray-900">
                Privacy Policy
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          <div className="prose prose-gray max-w-none">
            <div className="text-sm text-gray-500 mb-6">
              <strong>Last Updated:</strong> September 28, 2025
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              1. Introduction
            </h2>
            <p className="mb-6">
              SagaSwipe LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo;
              &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the Briefed
              service (&ldquo;Service&rdquo;). This Privacy Policy explains how
              we collect, use, disclose, and safeguard your information when you
              use our Service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              2. Information We Collect
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              2.1 Information You Provide Directly
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Account Information:</strong> Full name, email address,
                professional role, organization
              </li>
              <li>
                <strong>Professional Data:</strong> Bar number and state (for
                attorneys), contact information
              </li>
              <li>
                <strong>Authentication Data:</strong> Login credentials, Google
                OAuth data, profile information
              </li>
              <li>
                <strong>Usage Information:</strong> Search queries, case data
                accessed, research activities
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              2.2 Information We Collect Automatically
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Technical Information:</strong> IP address, device
                information, browser type, operating system
              </li>
              <li>
                <strong>Usage Analytics:</strong> Search patterns, feature
                usage, performance data, security data
              </li>
              <li>
                <strong>Cookies:</strong> Essential cookies for functionality,
                analytics cookies for insights
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide access to legal research tools and data</li>
              <li>Personalize your experience and recommendations</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send service-related notifications and updates</li>
              <li>Verify user identity and professional credentials</li>
              <li>Prevent fraud and unauthorized access</li>
              <li>Analyze usage patterns to improve our Service</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              4. Information Sharing and Disclosure
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              4.1 We Do Not Sell Your Personal Information
            </h3>
            <p className="mb-4">
              We do not sell, trade, or rent your personal information to third
              parties for marketing purposes.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              4.2 Limited Sharing
            </h3>
            <p className="mb-3">
              We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Service Providers:</strong> Payment processors (Stripe),
                cloud hosting (Firebase, Heroku), analytics services
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court
                order, or to protect our rights
              </li>
              <li>
                <strong>Professional Verification:</strong> Bar association
                verification, credential validation
              </li>
              <li>
                <strong>Aggregated Data:</strong> Anonymized data that cannot
                identify you personally
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              5. Data Security
            </h2>
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              5.1 Security Measures
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Employee training on data protection</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">
              5.2 Data Retention
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Account information: Retained while account is active</li>
              <li>Usage data: Retained for up to 2 years</li>
              <li>Court data: Retained as required by law</li>
              <li>Deleted data: Permanently removed from our systems</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              6. Your Rights and Choices
            </h2>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Access and Control:</strong> View and update your
                account information
              </li>
              <li>
                <strong>Data Portability:</strong> Download your data in
                portable format
              </li>
              <li>
                <strong>Account Deletion:</strong> Request complete deletion of
                your account
              </li>
              <li>
                <strong>Communication Preferences:</strong> Opt out of
                non-essential communications
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              7. California Privacy Rights (CCPA)
            </h2>
            <p className="mb-3">California residents have additional rights:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Right to know what personal information we collect</li>
              <li>Right to delete personal information</li>
              <li>Right to opt out of sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              8. European Users (GDPR)
            </h2>
            <p className="mb-3">
              European users have additional rights under GDPR:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Right of access to personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure (&ldquo;right to be forgotten&rdquo;)</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="mb-6">
              Our Service is not intended for children under 13. We do not
              knowingly collect information from children. If we discover such
              information, we will delete it immediately.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              10. Changes to This Privacy Policy
            </h2>
            <p className="mb-6">
              We may update this Privacy Policy periodically. Material changes
              will be communicated via email or Service notification. Continued
              use constitutes acceptance of changes.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              11. Contact Information
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <Mail className="h-6 w-6 text-gray-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    SagaSwipe LLC
                  </h3>
                  <p className="text-gray-700 mb-1">1240 Morningside Way</p>
                  <p className="text-gray-700 mb-1">Venice, CA 90291</p>
                  <p className="text-gray-700 mb-2">
                    Email:{' '}
                    <a
                      href="mailto:support@sagaswipe.com"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      support@sagaswipe.com
                    </a>
                  </p>
                  <p className="text-gray-700">
                    Privacy:{' '}
                    <a
                      href="mailto:privacy@sagaswipe.com"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      privacy@sagaswipe.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                <em>
                  This Privacy Policy describes how SagaSwipe LLC collects,
                  uses, and protects your information when you use the Briefed
                  service.
                </em>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
